import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NonFiscalsListResponse, NonFiscalModel } from './non-fiscals.model';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { PAGE_SIZE_DEFAULT, PAGE_SIZE_OPTIONS } from 'src/app/_common/constants';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NonFiscalsService } from './non-fiscals.service';
import { map } from 'rxjs/operators';
import { NonFiscalDocumentTypeEnum } from '../../_common/enums/non-fiscals.enum';
import { PreviewDialog, PreviewDialogData } from '../preview-dialog/preview.dialog';
import { faImage } from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ImageService } from '../../_common/services/image.service';
import { PrintPreviewService } from 'src/app/_common/services/print-preview.service';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'app-non-fiscals',
  templateUrl: './non-fiscals.component.html',
  styleUrls: ['./non-fiscals.component.scss']
})
export class NonFiscalsComponent implements OnInit {

  faPreview = faImage;

  containerTopValue: number = 0;

  showTable: boolean = false;

  pageEvent: PageEvent;

  pageIndex: number = 0;
  pageSize: number = PAGE_SIZE_DEFAULT;
  pageSizeOptions: number[] = PAGE_SIZE_OPTIONS;

  sortDirection: SortDirection = 'desc';
  sortColumn: string = 'printedDate';

  items: NonFiscalModel[] = [];
  totalRows: number;
  isPreviewed: number = null;

  updateTopValue(top: number) {
    this.containerTopValue = top;
  }

  calcContainerHeight(): string {
    return `calc(100% - ${this.containerTopValue}px)`;
  }

  nonFiscalDocumentTypeEnum: typeof NonFiscalDocumentTypeEnum = NonFiscalDocumentTypeEnum;

  displayedColumns: any[] = ['selection', 'shop', 'printer', 'documentNumber', 'printedDate', 'documentType', 'actions'];

  filter: any = {};
  selectedDocuments: Map<number, {fileId: number, type: number}> = new Map();
  allSelected: boolean = false;
  indeterminate: boolean = false;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  previewDialogData: PreviewDialogData = { image: null, isReceipt: false, fileId: null, items: null }

  constructor(
    private nonFiscalsService: NonFiscalsService,
    public dialog: MatDialog,
    public router: Router,
    private toastr: ToastrService,
    private translateService: TranslateService,
    private imageService: ImageService,
    private elementRef: ElementRef,
    private printPreviewService: PrintPreviewService
  ) { }

  ngOnInit(): void {
    this.printPreviewService.clearSelection();
  }

  onPaginateChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadData();
  }

  onSortData(event: Sort) {
    this.sortDirection = event.direction;
    this.sortColumn = event.active;
    this.pageIndex = 0;
    this.loadData();
  }

  applyFilters(event: any) {
    this.pageIndex = 0;
    this.filter = event;
    this.loadData();
  }

  onCheckboxChange(element: NonFiscalModel, event: MatCheckboxChange): void {
    element.selected = event.checked;
    
    this.updateSelectAllState();
    this.updateprintPreviewService();
  }

  onSelectAllChange(event: MatCheckboxChange): void {
    const isChecked = event.checked;
    
    this.items.forEach(item => {
      item.selected = isChecked;
    });
    
    this.updateSelectAllState();
    this.updateprintPreviewService();
  }

  updateSelectAllState(): void {
    const totalItems = this.items.length;
    const selectedItems = this.items.filter(item => item.selected);
    const selectedCount = selectedItems.length;
    
    this.allSelected = totalItems > 0 && selectedCount === totalItems;
    this.indeterminate = selectedCount > 0 && selectedCount < totalItems;
  }

  updateprintPreviewService(): void {
    const selectedDocuments = this.items
      .filter(item => item.selected)
      .map(item => ({ fileId: item.fileId, type: item.type }));
    
    this.printPreviewService.setSelectedDocuments(selectedDocuments);
  }

  loadData() {
    this.printPreviewService.clearSelection();

    this.nonFiscalsService
      .getNonFiscalsDocuments(this.pageIndex, this.pageSize, this.sortColumn, this.sortDirection, this.filter)
      .pipe(map((res: NonFiscalsListResponse) => {
        if (res.error && res.error.message) {
          this.toastr.error(res.error.message);
          return;
        }
        this.items = res.model.map(item => ({ ...item, selected: false }));
        this.totalRows = res.totalRows;
        this.showTable = true;
        this.updateSelectAllState();
      }))
      .subscribe();
  }

  filterData(filter: any) {
    this.filter = filter;
    this.pageIndex = 0;
    this.loadData();
  }

  previewOpenDialog(element: any) {
    this.isPreviewed = element.fileId;

    this.imageService.getImage(element.fileId, element.type).subscribe(async response => {
      if (response.status === 204) {
        this.toastr.error(this.translateService.instant('documents.preview-dialog.load-error'));
        this.isPreviewed = null;
        return;
      }

      this.previewDialogData.image = response.body;
      this.previewDialogData.fileId = element.fileId;
      this.previewDialogData.items = this.items.map(item => { return { fileId: item.fileId, type: item.type } });

      let dialogRef = this.dialog.open(PreviewDialog, {
        width: '420px',
        height: '95%',
        data: this.previewDialogData,
        autoFocus: false,
        panelClass: 'preview'
      });

      dialogRef.componentInstance.active.subscribe((fileId: any) => {
        this.isPreviewed = fileId;

        const activeElement = document.querySelector('.mat-row.active');
        if (activeElement) {
          activeElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });
        }
      });

      dialogRef.afterClosed().subscribe(() => {
        this.enableTextSelection(element);
        this.isPreviewed = null;
      });
    });
  }

  currentPreview(fileId: any): boolean {
    return this.isPreviewed == fileId;
  }

  onRowDoubleClick(row: any) {
    this.disableTextSelection(row);
    this.previewOpenDialog(row);
  }

  private disableTextSelection(element: any) {
    const clickedRow = this.elementRef.nativeElement.querySelector(`#row_${element.id}`);
    if (clickedRow) {
      clickedRow.classList.add('disable-text-selection');
    }
  }

  private enableTextSelection(element: any) {
    const clickedRow = this.elementRef.nativeElement.querySelector(`#row_${element.id}`);
    if (clickedRow) {
      window.getSelection().removeAllRanges();
      setTimeout(() => {
        clickedRow.classList.remove('disable-text-selection');
      }, 300);
    }
  }
}
